import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  CardsItemIcon,
  FilledCheckIcon,
  Image,
  LargeCloseIcon,
  MailMessageIcon,
  RawButton,
  Text,
  useResponsive,
} from '@common-fe/common-fe';

import CIPIllustration from '@/assets/dashboard/cip-illustration.svg';
import QuestionIllustration from '@/assets/illustrations/QuestionIllustration.svg';
import GridBackground from '@/assets/new-investments/grid-background.svg';
import { Modal } from '@/components';

interface Props {
  isVisible: boolean;
  onSetVisible: (value: boolean) => void;
  buttonNode?: React.ReactNode;
}

const LimitedAccessInfoModal: React.FC<Props> = ({ isVisible, onSetVisible, buttonNode }) => {
  const { isMobile } = useResponsive();
  const { t } = useTranslation();

  const handleToggleVisible = useCallback(() => {
    onSetVisible(!isVisible);
  }, [isVisible, onSetVisible]);

  return (
    <>
      <Modal
        visible={isVisible}
        testId="limited-access-info-modal"
        onSetVisible={handleToggleVisible}
        contentStyle={{
          padding: 0,
        }}
      >
        <Box
          style={{
            position: 'relative',
          }}
          overflow="hidden"
          round="moduleRound"
        >
          <Image
            src={GridBackground}
            style={{
              position: 'absolute',
              left: -20,
              top: -20,
              zIndex: 1,
            }}
          />
          <Box
            style={{
              zIndex: 2,
            }}
          >
            <Box
              pad={isMobile ? { top: 'spacing16', horizontal: 'spacing16', bottom: '0px' } : 'm'}
              direction="row"
              align="center"
              justify="end"
            >
              <RawButton
                data-testid="limited-access-info-modal_close-button"
                style={{ height: '24px' }}
                onClick={handleToggleVisible}
              >
                <LargeCloseIcon color="iconPrimary" size="24px"></LargeCloseIcon>
              </RawButton>
            </Box>
            <Box direction="column" align="center" justify="center">
              <Box margin={isMobile ? undefined : { horizontal: 'spacing24' }}>
                <Text
                  data-testid="limited-access-info-modal_title"
                  color="textTitle"
                  size={isMobile ? '3xl' : '4xl'}
                  textAlign="center"
                  weight={700}
                >
                  {t('Your Account Is Temporarily Limited')}
                </Text>
              </Box>
            </Box>
          </Box>
        </Box>

        <Box
          direction="column"
          margin={{ top: 'spacing32' }}
          pad={{ horizontal: isMobile ? 'spacing16' : 'spacing24' }}
        >
          <Box
            direction="column"
            align="center"
            overflow="hidden"
            round="container2Round"
            pad={
              isMobile
                ? { top: 'spacing16', horizontal: 'spacing16', bottom: '0px' }
                : { top: '54px', horizontal: 'spacing32' }
            }
            background="whatIsNew1"
          >
            <Text
              data-testid="limited-access-info-modal_subtitle"
              size="3xl"
              textAlign="center"
              weight={700}
              color="textOnColor"
            >
              {t("Your Account Isn't Verified.")}
            </Text>
            <Text
              data-testid="limited-access-info-modal_description"
              margin={{ top: 'spacing12' }}
              weight={400}
              textAlign="center"
              color="textOnColor"
            >
              {t(
                'You can view your account, but online and mobile features are locked until you verify your identity.'
              )}
            </Text>
            <Box
              data-testid="limited-access-info-modal_button-container"
              fill
              margin={{ top: 'spacing24' }}
              width={isMobile ? {} : { max: '280px' }}
            >
              {buttonNode}
            </Box>
            <Box fill={isMobile} margin={{ top: 'spacing24' }}>
              <Image width={isMobile ? '100%' : '280px'} src={CIPIllustration} />
            </Box>
          </Box>
          <Box direction={isMobile ? 'column' : 'row'} margin={{ top: isMobile ? 'spacing12' : 'spacing8' }}>
            <Box
              direction="column"
              align="center"
              round="container2Round"
              pad={isMobile ? 'spacing24' : 'spacing32'}
              background="whatIsNew2"
              margin={{ bottom: isMobile ? 'spacing12' : undefined }}
              style={{ flex: 1 }}
            >
              <Box>
                <Box direction={isMobile ? 'row' : 'column'}>
                  <Image width={isMobile ? '40px' : '72px'} src={QuestionIllustration} />

                  <Text
                    data-testid="limited-access-info-modal_why-required-title"
                    size="3xl"
                    margin={isMobile ? { left: 'spacing12' } : { top: 'spacing16' }}
                    color="textOnColor"
                    weight={700}
                  >
                    {t('Why Is Verification Required?')}
                  </Text>
                </Box>
                <Text
                  data-testid="limited-access-info-modal_why-required-description"
                  weight={400}
                  margin={{ top: 'spacing16' }}
                  color="textOnColor"
                  style={{ lineHeight: '20px' }}
                >
                  {t('This step protects your funds and helps prevent unauthorized access.')}
                </Text>
              </Box>
            </Box>
            <Box pad={{ left: 's' }}></Box>
            <Box
              align="center"
              pad={isMobile ? 'spacing24' : 'spacing32'}
              round="container2Round"
              background="whatIsNew3"
              style={{ flex: 1 }}
            >
              <Box direction="column">
                <Box direction={isMobile ? 'row' : 'column'}>
                  <Box height="72px" justify="center">
                    <FilledCheckIcon size={isMobile ? '40px' : '62px'} color="iconOnColor" />
                  </Box>

                  <Text
                    data-testid="limited-access-info-modal_how-to-verify-title"
                    size="3xl"
                    margin={isMobile ? { left: 'spacing12' } : { top: 'spacing16' }}
                    color="textOnColor"
                    weight={700}
                  >
                    {t('How To Verify')}
                  </Text>
                </Box>
                <Box
                  data-testid="limited-access-info-modal_email-method"
                  direction="row"
                  margin={{ top: 'spacing12' }}
                >
                  <MailMessageIcon size="24px" color="iconOnColor" />
                  <Text weight={400} size="medium" color="textOnColor" margin={{ left: 'spacing8' }}>
                    {t("Enter your employer-provided email. We'll send a one-time code.")}
                  </Text>
                </Box>

                <Box
                  data-testid="limited-access-info-modal_card-method"
                  direction="row"
                  margin={{ top: 'spacing8' }}
                >
                  <CardsItemIcon size="24px" color="iconOnColor" />
                  <Text weight={400} size="medium" color="textOnColor" margin={{ left: 'spacing8' }}>
                    {t('Enter the last 4 digits of your payment card.')}
                  </Text>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        <Box
          data-testid="limited-access-info-modal_footer-button"
          direction="column"
          pad={isMobile ? 'spacing16' : 'spacing24'}
        >
          {buttonNode}
        </Box>
      </Modal>
    </>
  );
};
export default LimitedAccessInfoModal;
